import { memo, useEffect, useReducer, useState } from "react";
import { motion } from "framer-motion";
import { Ear, Flag, GitBranch, UserCheck } from "lucide-react";
import type { DemoCallDoc } from "../demo/demo-live-provider";
import { getManualFlag } from "../lib/flags-store";

const TIER_STYLE: Record<string, string> = {
  red: "border-l-[#ef4444] bg-[#fef2f2] dark:bg-[#450a0a]/35 border-red-200 dark:border-red-900/50",
  amber: "border-l-[#f59e0b] bg-[#fffbeb] dark:bg-[#451a03]/35 border-amber-200 dark:border-amber-900/50",
  green: "border-l-border bg-card border-border",
};

const STATE_BADGE: Record<string, string> = {
  dialing: "DIALING",
  ringing: "RINGING",
  supervisor_listening: "LISTENING",
  supervisor_steering: "STEERING",
  human_controlled: "HUMAN",
  wrapping: "WRAPPING",
};

function Elapsed({ since }: { since?: string }) {
  const [, force] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    const i = setInterval(force, 1000);
    return () => clearInterval(i);
  }, []);
  const s = Math.max(0, Math.floor((Date.now() - new Date(since || Date.now()).getTime()) / 1000));
  return (
    <span className="font-mono tnum text-[11px] text-foreground">
      {String(Math.floor(s / 60)).padStart(2, "0")}:{String(s % 60).padStart(2, "0")}
    </span>
  );
}

function Spark({ data }: { data?: number[] }) {
  const w = 56, h = 16;
  if (!data || data.length < 2) return <div style={{ width: w, height: h }} />;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v + 1) / 2) * h}`).join(" ");
  return (
    <svg width={w} height={h} className="opacity-70">
      <polyline points={pts} fill="none" stroke="currentColor" strokeWidth="1" className="text-muted-foreground" />
    </svg>
  );
}

export interface CallTileV2Props {
  call: DemoCallDoc;
  ruleLabels?: Record<string, string>;
  onOpen: (id: string) => void;
}

function CallTileV2Impl({ call, ruleLabels = {}, onOpen }: CallTileV2Props) {
  const tier = call.risk_tier || "green";
  const turns = (call.turns || []).slice(-3);
  const topRule = call.risk_reasons?.length
    ? ruleLabels[call.risk_reasons[call.risk_reasons.length - 1]] || call.risk_reasons[call.risk_reasons.length - 1]
    : null;
  const badge = STATE_BADGE[call.state];
  const isFlagged = Boolean(getManualFlag(call.id));

  return (
    <motion.div
      layout
      layoutId={call.id}
      transition={{ type: "spring", stiffness: 260, damping: 30 }}
      data-testid={`call-tile-${call.id}`}
      data-tier={tier}
      onClick={() => onOpen(call.id)}
      className={`relative border border-border border-l-4 ${TIER_STYLE[tier] || TIER_STYLE.green} p-2 h-full min-h-[140px] flex flex-col justify-between cursor-pointer select-none hover:brightness-[0.98] transition overflow-hidden rounded-sm`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <Elapsed since={call.started_at} />
        <span className="text-[10px] font-mono px-1 border border-border text-muted-foreground uppercase">{call.language || "hi"}</span>
        <span className="text-[10px] text-muted-foreground truncate">{call.campaign_name || "General"}</span>
        {(call as any).is_real && (
          <span className="flex items-center gap-1 text-[9px] font-mono font-bold px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 shrink-0">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            LIVE
          </span>
        )}
        <span className="ml-auto flex items-center gap-1">
          {isFlagged && <Flag size={11} className="text-amber-500 fill-current" title="Flagged for review" />}
          {call.state === "human_controlled" && <UserCheck size={11} className="text-primary" />}
          {call.state === "supervisor_listening" && <Ear size={11} className="text-primary" />}
          {call.state === "supervisor_steering" && <GitBranch size={11} className="text-primary" />}
          {badge && (
            <span
              className={`text-[9px] font-mono px-1 ${
                call.state === "human_controlled" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
              }`}
            >
              {badge}
            </span>
          )}
        </span>
      </div>
      <div className="flex items-center gap-2 mt-0.5">
        <span className="text-[10px] font-mono text-muted-foreground truncate">{call.customer_ref || call.customer_name}</span>
        <span
          className={`ml-auto text-[10px] font-mono tnum ${
            tier === "red" ? "text-risk-red font-semibold" : tier === "amber" ? "text-risk-amber font-semibold" : "text-muted-foreground"
          }`}
        >
          {call.risk_score}
        </span>
      </div>
      <div className="mt-1 flex-1 min-h-0 flex flex-col justify-end gap-px overflow-hidden">
        {turns.length === 0 && <div className="text-[10px] text-muted-foreground italic">waiting for audio…</div>}
        {turns.map((t, i) => (
          <div
            key={t.seq}
            className={`text-[10px] leading-[13px] truncate ${
              i === turns.length - 1 ? "text-foreground font-medium" : "text-muted-foreground"
            }`}
          >
            <span
              className={`font-mono mr-1 font-semibold ${
                t.speaker === "ai" ? "text-muted-foreground" : t.speaker === "customer" ? "text-foreground" : "text-primary"
              }`}
            >
              {t.speaker === "ai" ? "AI" : t.speaker === "customer" ? "CU" : "SV"}
            </span>
            {t.flags?.includes("steer") ? `STEER -> ${t.text}` : t.text}
          </div>
        ))}
      </div>
      <div className="flex items-end justify-between mt-1 pt-1 border-t border-border/40">
        <Spark data={call.sentiment_trend} />
        {topRule && (
          <span
            className={`text-[9px] font-mono truncate max-w-[130px] font-medium ${
              tier === "red" ? "text-risk-red" : "text-risk-amber"
            }`}
            data-testid={`tile-rule-${call.id}`}
          >
            {topRule}
          </span>
        )}
      </div>
    </motion.div>
  );
}

export const CallTileV2 = memo(CallTileV2Impl);
